import { useEffect, useMemo, useState } from 'react';
import { marketingWebAPI } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Users, Mail, MousePointerClick, UserMinus, Send, Plus, X, ExternalLink } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const fmtNum = (n) => (n ?? 0).toLocaleString('es-ES');
const fmtPct = (v) => (v == null ? '—' : `${(v * 100).toFixed(2)}%`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
};

function KpiCard({ label, value, icon: Icon, hint }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function MarketingWebEmail() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  // Crear borrador de campaña
  const [modalOpen, setModalOpen] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [cargandoPlantillas, setCargandoPlantillas] = useState(false);
  const [form, setForm] = useState({ subject: '', preview_text: '', from_name: '', template_id: '' });
  const [creando, setCreando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);
  const [pruebaMsg, setPruebaMsg] = useState(null);

  const abrirModal = async () => {
    setModalOpen(true);
    setResultado(null);
    setModalError(null);
    setPruebaMsg(null);
    setTestEmail(user?.email || '');
    setForm({ subject: '', preview_text: '', from_name: '', template_id: '' });
    setCargandoPlantillas(true);
    try {
      const res = await marketingWebAPI.emailPlantillas();
      setPlantillas(res.data?.plantillas || []);
    } catch (e) {
      setModalError(e?.response?.data?.detail || 'No se pudieron cargar las plantillas');
    } finally {
      setCargandoPlantillas(false);
    }
  };

  const crearBorrador = async () => {
    setCreando(true);
    setModalError(null);
    try {
      const res = await marketingWebAPI.emailCrearCampana({
        subject: form.subject,
        template_id: Number(form.template_id),
        preview_text: form.preview_text || undefined,
        from_name: form.from_name || undefined,
      });
      setResultado(res.data);
      setPruebaMsg(null);
    } catch (e) {
      setModalError(e?.response?.data?.detail || 'No se pudo crear el borrador');
    } finally {
      setCreando(false);
    }
  };

  const enviarPrueba = async () => {
    if (!resultado?.id || !testEmail.trim()) return;
    setEnviandoPrueba(true);
    setPruebaMsg(null);
    setModalError(null);
    try {
      await marketingWebAPI.emailEnviarPrueba(resultado.id, [testEmail.trim()]);
      setPruebaMsg({ ok: true, text: `Prueba enviada a ${testEmail.trim()}. Revisa tu bandeja (y Spam).` });
    } catch (e) {
      setPruebaMsg({ ok: false, text: e?.response?.data?.detail || 'No se pudo enviar la prueba' });
    } finally {
      setEnviandoPrueba(false);
    }
  };

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingWebAPI.email();
      setData(res.data);
    } catch (e) {
      console.error('Error Mailchimp:', e);
      setError(e?.response?.data?.detail || 'Error cargando Mailchimp');
    } finally {
      setLoading(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      await marketingWebAPI.emailSync();
      await cargar();
    } catch (e) {
      console.error('Error sincronizando Mailchimp:', e);
      setError(e?.response?.data?.detail || 'Error sincronizando Mailchimp');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const snapshot = data?.snapshot;
  const growth = data?.growth_history || [];

  // Mailchimp deprecó existing/imports/optins en growth-history (devuelven
  // siempre 0). El total real de suscriptores por mes está en `subscribed`;
  // las altas netas del mes se calculan como la variación frente al mes previo.
  const growthChart = useMemo(() => {
    if (!growth.length) return null;
    const puntos = growth.map((g) => ({ month: g.month, total: g.subscribed || 0 }));
    // growth-history solo llega al último mes cerrado; añadimos el total vivo
    // (member_count actual) como punto del mes en curso para ver el dato de hoy.
    if (snapshot?.total_subscribers != null) {
      const mesActual = (snapshot.fecha || '').slice(0, 7);
      const ultimo = puntos[puntos.length - 1];
      if (mesActual && (!ultimo || ultimo.month < mesActual)) {
        puntos.push({ month: mesActual, total: snapshot.total_subscribers });
      } else if (ultimo && ultimo.month === mesActual) {
        ultimo.total = snapshot.total_subscribers;
      }
    }
    const totales = puntos.map((p) => p.total);
    const netas = totales.map((t, i) => (i === 0 ? null : t - totales[i - 1]));
    return {
      labels: puntos.map((p) => p.month),
      datasets: [
        {
          label: 'Suscriptores',
          data: totales,
          borderColor: '#1A9169',
          backgroundColor: 'rgba(26, 145, 105, 0.12)',
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Altas netas/mes',
          data: netas,
          borderColor: '#67CEA9',
          backgroundColor: 'rgba(103, 206, 169, 0.10)',
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
        },
      ],
    };
  }, [growth, snapshot]);

  // Altas netas del último mes: total vivo actual menos el último mes cerrado
  // del growth-history (el member_count_since_send de Mailchimp suele venir a 0).
  const altasUltimoMes = useMemo(() => {
    if (!growth.length) return null;
    const ultimoCerrado = growth[growth.length - 1]?.subscribed || 0;
    const actual = snapshot?.total_subscribers;
    if (actual != null) return actual - ultimoCerrado;
    if (growth.length < 2) return null;
    return ultimoCerrado - (growth[growth.length - 2]?.subscribed || 0);
  }, [growth, snapshot]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-duetto-primary">Email Marketing · Mailchimp</h1>
          <p className="text-sm text-gray-500 mt-1">
            Audiencia <span className="font-semibold">{snapshot?.audience_name || '—'}</span> · datos de Mailchimp
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={cargar}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </button>
          <button
            onClick={sync}
            disabled={syncing}
            className="px-3 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 disabled:opacity-50"
            title="Forzar sincronización con Mailchimp ahora"
          >
            {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
          </button>
          <button
            onClick={abrirModal}
            className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:opacity-90 flex items-center gap-2"
            title="Crear un borrador de campaña en Mailchimp (no envía nada)"
          >
            <Plus className="w-4 h-4" />
            Nuevo envío (borrador)
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 text-sm text-yellow-800">
          {error}. Verifica que MAILCHIMP_API_KEY, MAILCHIMP_SERVER_PREFIX y MAILCHIMP_AUDIENCE_ID están configurados en el .env del ERP.
        </div>
      )}

      {!snapshot && !loading && !error && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-6 text-center text-sm text-blue-800">
          Aún no hay datos sincronizados. Pulsa <strong>Sincronizar ahora</strong> para traer el estado actual de Mailchimp.
        </div>
      )}

      {snapshot && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <KpiCard label="Suscriptores" value={fmtNum(snapshot.total_subscribers)} icon={Users} />
            <KpiCard
              label="Altas netas último mes"
              value={altasUltimoMes != null ? `${altasUltimoMes >= 0 ? '+' : ''}${fmtNum(altasUltimoMes)}` : `+${fmtNum(snapshot.new_subscribers_last_month)}`}
              icon={Users}
            />
            <KpiCard label="Open rate medio" value={fmtPct(snapshot.avg_open_rate)} icon={Mail} hint="Media de la audience" />
            <KpiCard label="Click rate medio" value={fmtPct(snapshot.avg_click_rate)} icon={MousePointerClick} />
            <KpiCard label="Campañas enviadas" value={fmtNum(snapshot.campaigns_sent_total)} icon={Send} />
            <KpiCard label="Bajas totales" value={fmtNum(snapshot.unsubscribes_total)} icon={UserMinus} />
          </div>

          {/* Growth chart */}
          {growthChart && (
            <div className="bg-white border rounded-lg p-5 mb-6">
              <h2 className="text-base font-semibold mb-3 text-gray-700">Evolución de la audiencia (últimos 36 meses)</h2>
              <div className="h-72">
                <Line
                  data={growthChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: { position: 'top' } },
                    scales: {
                      y: { type: 'linear', position: 'left', beginAtZero: false, title: { display: true, text: 'Suscriptores' } },
                      y1: { type: 'linear', position: 'right', beginAtZero: false, grid: { drawOnChartArea: false }, title: { display: true, text: 'Altas netas/mes' } },
                    },
                  }}
                />
              </div>
            </div>
          )}

          {/* Últimas campañas */}
          <div className="bg-white border rounded-lg p-5">
            <h2 className="text-base font-semibold mb-3 text-gray-700">Últimas campañas enviadas</h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asunto</TableHead>
                  <TableHead className="text-right">Enviada</TableHead>
                  <TableHead className="text-right">Enviados</TableHead>
                  <TableHead className="text-right">Open rate</TableHead>
                  <TableHead className="text-right">Click rate</TableHead>
                  <TableHead className="text-right">Bajas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(snapshot.last_campaigns || []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm">
                      <div className="font-medium">{c.titulo}</div>
                      {c.nombre && c.nombre !== c.titulo && (
                        <div className="text-xs text-gray-500">{c.nombre}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs whitespace-nowrap">{fmtDate(c.enviada_en)}</TableCell>
                    <TableCell className="text-right text-sm">{fmtNum(c.enviados)}</TableCell>
                    <TableCell className="text-right text-sm">{fmtPct(c.open_rate)}</TableCell>
                    <TableCell className="text-right text-sm">{fmtPct(c.click_rate)}</TableCell>
                    <TableCell className="text-right text-sm">{fmtNum(c.unsubs)}</TableCell>
                  </TableRow>
                ))}
                {(!snapshot.last_campaigns || snapshot.last_campaigns.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-400 py-4">
                      No hay campañas enviadas todavía.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <p className="text-xs text-gray-400 mt-4 text-right">
            Último snapshot: {snapshot.fecha} · sincroniza automáticamente cada noche a las 04:30
          </p>
        </>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !creando && setModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Nuevo envío · borrador en Mailchimp</h3>
              <button onClick={() => !creando && setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {!resultado && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-800">
                  Esto crea la campaña <strong>como borrador</strong> en Mailchimp con la plantilla elegida.
                  No se envía nada: la revisas, ajustas el diseño y la envías tú desde Mailchimp.
                </div>
              )}

              {modalError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{modalError}</div>
              )}

              {resultado ? (
                <div className="space-y-3 text-center py-4">
                  <div className="text-green-700 font-semibold">Borrador creado en Mailchimp</div>
                  <div className="text-sm text-gray-600">
                    Asunto: <span className="font-medium">{resultado.subject}</span><br />
                    Remitente: {resultado.from_name} · {resultado.reply_to}<br />
                    Destinatarios: {fmtNum(resultado.destinatarios)} suscriptores
                  </div>
                  {resultado.edit_url && (
                    <a
                      href={resultado.edit_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Abrir en Mailchimp para revisar y enviar
                    </a>
                  )}

                  {/* Envío de prueba (solo a la dirección indicada) */}
                  <div className="border-t pt-4 mt-4 text-left">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Enviar una prueba (solo a esta dirección)</label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="tu@correo.com"
                        className="flex-1 border rounded-md px-3 py-2 text-sm"
                      />
                      <button
                        onClick={enviarPrueba}
                        disabled={enviandoPrueba || !testEmail.trim()}
                        className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:opacity-90 disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        {enviandoPrueba ? 'Enviando…' : 'Enviar prueba'}
                      </button>
                    </div>
                    {pruebaMsg && (
                      <div className={`mt-2 text-sm ${pruebaMsg.ok ? 'text-green-700' : 'text-red-700'}`}>
                        {pruebaMsg.text}
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-1">La prueba no se envía a la audiencia, solo a la dirección que pongas aquí.</p>
                  </div>

                  <div>
                    <button onClick={() => setModalOpen(false)} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 mt-2">
                      Cerrar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Asunto del email *</label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      placeholder="Ej.: Novedades de temporada en Duetto Catering"
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Texto de vista previa (opcional)</label>
                    <input
                      type="text"
                      value={form.preview_text}
                      onChange={(e) => setForm({ ...form, preview_text: e.target.value })}
                      placeholder="Frase que se ve junto al asunto en la bandeja"
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Plantilla *</label>
                    {cargandoPlantillas ? (
                      <div className="text-sm text-gray-400 py-2">Cargando plantillas…</div>
                    ) : plantillas.length === 0 ? (
                      <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                        No hay plantillas guardadas en Mailchimp. Crea una primero en Mailchimp (Content → Email templates).
                      </div>
                    ) : (
                      <select
                        value={form.template_id}
                        onChange={(e) => setForm({ ...form, template_id: e.target.value })}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                      >
                        <option value="">— Selecciona una plantilla —</option>
                        {plantillas.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre del remitente (opcional)</label>
                    <input
                      type="text"
                      value={form.from_name}
                      onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                      placeholder="Por defecto, el de la audiencia en Mailchimp"
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setModalOpen(false)}
                      disabled={creando}
                      className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={crearBorrador}
                      disabled={creando || !form.subject.trim() || !form.template_id}
                      className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:opacity-90 disabled:opacity-50"
                    >
                      {creando ? 'Creando…' : 'Crear borrador'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
